<template>
  <div class="pour-container" ref="container">
    <SharedProgressBar
      v-if="phase === 'playing'"
      :total-score="totalScore"
      :target="target"
      :players="players"
      :combo="comboCount"
      :won="gameWon"
    />

    <!-- Round info -->
    <div v-if="phase === 'playing'" class="round-info">
      <span class="text-white/40 text-xs">Runde {{ roundNum }} / {{ maxRounds }}</span>
      <span class="text-yellow-400 text-xs font-bold">Perfect Zone: {{ perfectMin }}%–{{ perfectMax }}%</span>
    </div>

    <!-- Canvas -->
    <canvas v-show="phase === 'playing'" ref="canvas" class="pour-canvas"
      @touchstart.prevent="onPressStart"
      @touchend.prevent="onPressEnd"
      @mousedown.prevent="onPressStart"
      @mouseup.prevent="onPressEnd"
    ></canvas>

    <!-- Fill percentage HUD -->
    <div v-if="phase === 'playing'" class="fill-hud">
      <span class="text-3xl font-mono font-black" :class="fillColor">{{ Math.round(fillPercent) }}%</span>
      <p v-if="isPouring" class="text-white/40 text-xs mt-1">Halten zum Gießen...</p>
      <p v-else class="text-white/30 text-xs mt-1">Finger drücken & halten</p>
    </div>

    <!-- Round Result Flash -->
    <Transition name="fade">
      <div v-if="roundResult" class="round-result" :class="roundResult.cls">
        <span class="text-4xl">{{ roundResult.emoji }}</span>
        <p class="font-bold text-lg mt-1">{{ roundResult.text }}</p>
        <p class="text-sm opacity-60">+{{ roundResult.points }} Punkte</p>
      </div>
    </Transition>

    <!-- Idle -->
    <div v-if="phase === 'idle'" class="overlay">
      <div class="text-5xl mb-4">🥂</div>
      <h2 class="text-2xl font-bold text-white mb-2">The Golden Pour</h2>
      <p class="text-white/60 text-sm mb-1">Halte gedrückt, um das Glas zu füllen.</p>
      <p class="text-white/40 text-xs mb-6">Stoppe exakt in der Perfect Zone!</p>
      <div class="flex gap-3">
        <button @click="startSolo" class="start-btn">🎯 Solo</button>
        <button @click="startMultiplayer" class="start-btn multiplayer-btn">👥 Tisch-Modus</button>
      </div>
    </div>

    <!-- Lobby -->
    <div v-if="phase === 'lobby'" class="overlay">
      <div class="text-4xl mb-3">🥂</div>
      <h2 class="text-xl font-bold text-white mb-1">Golden Pour – Lobby</h2>
      <p class="text-white/50 text-xs mb-4">Warte, bis alle am Tisch beigetreten sind</p>

      <div class="lobby-players">
        <div v-for="p in players" :key="p.id" class="lobby-player">
          <div class="lobby-avatar" :class="{ 'host-ring': p.isHost }">
            {{ (p.name || '?')[0].toUpperCase() }}
          </div>
          <span class="text-white text-sm">{{ p.name }}</span>
          <span v-if="p.isHost" class="text-yellow-400 text-[10px] ml-1">HOST</span>
        </div>
      </div>

      <p class="text-white/40 text-xs mt-3 mb-4">
        {{ players.length }} Spieler · Ziel: {{ target }} Perfect Pours
      </p>

      <button
        v-if="isHost"
        @click="launchGame"
        class="start-btn w-full"
      >Spiel starten ({{ players.length }} Spieler)</button>

      <p v-else class="text-white/50 text-sm animate-pulse">Warte auf Host...</p>
    </div>

    <!-- Result -->
    <div v-if="phase === 'result'" class="overlay">
      <div class="text-5xl mb-4">{{ gameWon ? '🏆' : '🍷' }}</div>
      <h2 class="text-2xl font-bold text-white mb-2">{{ gameWon ? 'Perfekt!' : 'Gut gemacht!' }}</h2>
      <p class="text-white/60 mb-1">{{ perfectPours }} Perfect Pours</p>
      <p class="text-white/40 text-sm mb-4">Score: {{ myScore }}</p>
      <div v-if="reward" class="reward-box mb-4">
        <p class="text-yellow-400 font-bold">{{ reward.message }}</p>
      </div>
      <button @click="$emit('close')" class="start-btn">Schließen</button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted, nextTick } from 'vue'
import SharedProgressBar from './SharedProgressBar.vue'
import { gamificationService } from '@/services/api/gamificationService'

const props = defineProps({
  existingGameId: { type: String, default: null },
})
const emit = defineEmits(['close', 'apply-discount'])
const container = ref(null)
const canvas = ref(null)
let ctx = null
let animFrame = null

const phase = ref('idle')
const myScore = ref(0)
const totalScore = ref(0)
const target = ref(5)
const comboCount = ref(0)
const gameWon = ref(false)
const players = ref([])
const reward = ref(null)
const isHost = ref(false)

const roundNum = ref(0)
const maxRounds = ref(8)
const perfectPours = ref(0)
const perfectMin = ref(93)
const perfectMax = ref(97)

const fillPercent = ref(0)
const isPouring = ref(false)
const roundResult = ref(null)
const fillColor = ref('text-white')

let pourSpeed = 0
let pourAccel = 0
let pourTimer = null
let gameSessionId = null
let glassShattered = false
let lobbyPollInterval = null
const myId = localStorage.getItem('sessionUniqueId') || localStorage.getItem('sessionId') || ''

// --- Lobby & Session ---

async function startSolo() {
  await createOrJoinSession()
  beginPlaying()
}

async function startMultiplayer() {
  await createOrJoinSession()
  phase.value = 'lobby'
  isHost.value = !props.existingGameId
  startLobbyPolling()
}

async function createOrJoinSession() {
  const tableId = parseInt(localStorage.getItem('tableId')) || 0
  try {
    let data
    if (props.existingGameId) {
      const res = await gamificationService.joinMiniGame(props.existingGameId, {
        sessionIdentifier: myId, tableId, playerName: 'Spieler',
      })
      data = res.data
      isHost.value = false
    } else {
      const res = await gamificationService.createMiniGameSession({
        gameType: 'golden_pour', tableId, sessionIdentifier: myId,
        orderSessionId: parseInt(localStorage.getItem('sessionNumericId')) || null,
      })
      data = res.data
      isHost.value = true
    }
    gameSessionId = data.id
    target.value = data.target
    maxRounds.value = Math.max(data.target || 5, 5)
    players.value = data.players || []
  } catch (e) { target.value = 5; maxRounds.value = 8 }
}

function startLobbyPolling() {
  stopLobbyPolling()
  lobbyPollInterval = setInterval(async () => {
    if (!gameSessionId || phase.value !== 'lobby') return
    try {
      const { data } = await gamificationService.getMiniGameSession(gameSessionId)
      if (!data) return
      players.value = data.players || []
      target.value = data.target
      maxRounds.value = Math.max(data.target || 5, 5)
      if (data.status === 'playing') { stopLobbyPolling(); beginPlaying() }
    } catch (e) { /* retry */ }
  }, 2000)
}

function stopLobbyPolling() {
  if (lobbyPollInterval) { clearInterval(lobbyPollInterval); lobbyPollInterval = null }
}

async function launchGame() {
  if (!gameSessionId) return
  try { await gamificationService.startMiniGame(gameSessionId) } catch (e) { /* */ }
  stopLobbyPolling()
  beginPlaying()
}

onMounted(() => {
  if (props.existingGameId) startMultiplayer()
})

// --- Game Logic ---

function beginPlaying() {
  phase.value = 'playing'
  myScore.value = 0
  totalScore.value = 0
  comboCount.value = 0
  perfectPours.value = 0
  roundNum.value = 1
  fillPercent.value = 0
  glassShattered = false
  gameWon.value = false
  reward.value = null
  perfectMin.value = 93
  perfectMax.value = 97

  nextTick(() => {
    setupCanvas()
    drawGlass()
  })
}

function setupCanvas() {
  if (!canvas.value || !container.value) return
  const rect = container.value.getBoundingClientRect()
  const w = rect.width
  const h = Math.min(rect.height - 120, 400)
  canvas.value.width = w
  canvas.value.height = h
  canvas.value.style.width = w + 'px'
  canvas.value.style.height = h + 'px'
  ctx = canvas.value.getContext('2d')
}

function drawGlass() {
  if (!ctx || !canvas.value) return
  const w = canvas.value.width
  const h = canvas.value.height
  ctx.clearRect(0, 0, w, h)

  const cx = w / 2
  const glassW = 90
  const glassH = 180
  const glassTop = h * 0.12
  const glassBot = glassTop + glassH

  ctx.save()
  ctx.strokeStyle = 'rgba(255,255,255,0.12)'
  ctx.lineWidth = 2
  ctx.beginPath()
  ctx.moveTo(cx - glassW / 2, glassTop)
  ctx.lineTo(cx - glassW * 0.35, glassBot)
  ctx.lineTo(cx + glassW * 0.35, glassBot)
  ctx.lineTo(cx + glassW / 2, glassTop)
  ctx.closePath()
  ctx.stroke()

  const pMinY = glassBot - (perfectMin.value / 100) * glassH
  const pMaxY = glassBot - (perfectMax.value / 100) * glassH
  ctx.fillStyle = 'rgba(46,204,113,0.08)'
  ctx.fillRect(cx - glassW / 2 - 10, pMaxY, glassW + 20, pMinY - pMaxY)
  ctx.strokeStyle = 'rgba(46,204,113,0.3)'
  ctx.lineWidth = 1
  ctx.setLineDash([4, 4])
  ctx.beginPath()
  ctx.moveTo(cx - glassW / 2 - 10, pMinY)
  ctx.lineTo(cx + glassW / 2 + 10, pMinY)
  ctx.moveTo(cx - glassW / 2 - 10, pMaxY)
  ctx.lineTo(cx + glassW / 2 + 10, pMaxY)
  ctx.stroke()
  ctx.setLineDash([])

  if (fillPercent.value > 0 && !glassShattered) {
    const fillH = (fillPercent.value / 100) * glassH
    const fillTop = glassBot - fillH

    const grad = ctx.createLinearGradient(cx, fillTop, cx, glassBot)
    grad.addColorStop(0, 'rgba(255,215,0,0.9)')
    grad.addColorStop(0.5, 'rgba(218,165,32,0.85)')
    grad.addColorStop(1, 'rgba(184,134,11,0.8)')

    const waveTime = Date.now() / 600
    ctx.beginPath()
    ctx.moveTo(cx - glassW * 0.35, glassBot)

    const leftFillTop = cx - glassW * 0.35 + (1 - fillH / glassH) * (glassW * 0.15)
    const rightFillTop = cx + glassW * 0.35 - (1 - fillH / glassH) * (glassW * 0.15)
    ctx.lineTo(leftFillTop, fillTop)

    for (let x = leftFillTop; x <= rightFillTop; x += 2) {
      const wave = Math.sin((x - leftFillTop) * 0.06 + waveTime) * 3 * (isPouring.value ? 1.5 : 0.5)
      ctx.lineTo(x, fillTop + wave)
    }

    ctx.lineTo(cx + glassW * 0.35, glassBot)
    ctx.closePath()
    ctx.fillStyle = grad
    ctx.fill()

    ctx.strokeStyle = 'rgba(255,255,255,0.15)'
    ctx.lineWidth = 1
    ctx.beginPath()
    for (let x = leftFillTop; x <= rightFillTop; x += 2) {
      const wave = Math.sin((x - leftFillTop) * 0.06 + waveTime) * 3 * (isPouring.value ? 1.5 : 0.5)
      if (x === leftFillTop) ctx.moveTo(x, fillTop + wave)
      else ctx.lineTo(x, fillTop + wave)
    }
    ctx.stroke()
  }

  if (glassShattered) {
    ctx.save()
    ctx.globalAlpha = 0.6
    for (let i = 0; i < 12; i++) {
      const sx = cx + (Math.random() - 0.5) * glassW * 1.5
      const sy = glassTop + Math.random() * glassH
      ctx.fillStyle = 'rgba(255,255,255,0.2)'
      ctx.fillRect(sx, sy, 4 + Math.random() * 8, 2 + Math.random() * 6)
    }
    ctx.restore()
  }

  ctx.restore()
  if (phase.value === 'playing') animFrame = requestAnimationFrame(drawGlass)
}

function updateFillColor() {
  const f = fillPercent.value
  if (f >= perfectMin.value && f <= perfectMax.value) fillColor.value = 'text-green-400'
  else if (f > perfectMax.value) fillColor.value = 'text-red-400'
  else fillColor.value = 'text-white'
}

function onPressStart() {
  if (phase.value !== 'playing' || roundResult.value) return
  isPouring.value = true
  pourSpeed = 0.3 + Math.random() * 0.4
  pourAccel = 0.01 + Math.random() * 0.02

  pourTimer = setInterval(() => {
    pourSpeed += pourAccel * (Math.sin(Date.now() / 300) * 0.5 + 0.5)
    fillPercent.value = Math.min(101, fillPercent.value + pourSpeed)
    updateFillColor()
    if (fillPercent.value >= 101) onOverflow()
  }, 16)
}

function onPressEnd() {
  if (!isPouring.value) return
  isPouring.value = false
  clearInterval(pourTimer)
  if (phase.value !== 'playing' || roundResult.value) return
  evaluatePour()
}

function onOverflow() {
  clearInterval(pourTimer)
  isPouring.value = false
  glassShattered = true
  if (navigator.vibrate) navigator.vibrate([100, 50, 100])
  showRoundResult('overflow')
}

function evaluatePour() {
  const f = fillPercent.value
  if (f >= perfectMin.value && f <= perfectMax.value) showRoundResult('perfect')
  else if (f >= perfectMin.value - 5 && f < perfectMin.value) showRoundResult('good')
  else if (f > perfectMax.value && f < 101) showRoundResult('close')
  else showRoundResult('miss')
}

function showRoundResult(type) {
  const results = {
    perfect: { emoji: '✨', text: 'PERFECT!', points: 10, cls: 'result-perfect' },
    good: { emoji: '👍', text: 'Fast perfekt!', points: 6, cls: 'result-good' },
    close: { emoji: '😬', text: 'Knapp drüber...', points: 3, cls: 'result-close' },
    miss: { emoji: '💧', text: 'Zu wenig...', points: 1, cls: 'result-miss' },
    overflow: { emoji: '💥', text: 'Überlaufen!', points: 0, cls: 'result-overflow' },
  }
  const r = results[type]
  roundResult.value = r

  if (type === 'perfect') {
    perfectPours.value++
    comboCount.value++
    if (navigator.vibrate) navigator.vibrate(50)
  } else {
    comboCount.value = 0
  }

  myScore.value += r.points
  totalScore.value = myScore.value
  syncScore(type !== 'perfect')

  setTimeout(() => { roundResult.value = null; nextRound() }, 1500)
}

function nextRound() {
  roundNum.value++
  if (roundNum.value > maxRounds.value) { endGame(); return }
  fillPercent.value = 0
  glassShattered = false
  pourSpeed = 0
  perfectMin.value = 90 + Math.floor(Math.random() * 5)
  perfectMax.value = perfectMin.value + 3 + Math.floor(Math.random() * 2)
  updateFillColor()
}

async function syncScore(comboBreak = false) {
  if (!gameSessionId) return
  try {
    const { data } = await gamificationService.submitMiniGameScore(gameSessionId, {
      sessionIdentifier: myId, score: myScore.value, comboBreak,
    })
    totalScore.value = data.totalScore
    players.value = data.players || []
    if (data.won) gameWon.value = true
  } catch (e) { /* offline */ }
}

async function endGame() {
  cancelAnimationFrame(animFrame)
  phase.value = 'result'
  gameWon.value = perfectPours.value >= target.value

  if (gameSessionId) {
    try {
      const { data } = await gamificationService.finishMiniGame(gameSessionId)
      reward.value = data.reward
      if (data.reward) emit('apply-discount', { ...data.reward, gameName: 'Golden Pour' })
    } catch (e) { /* */ }
  }
}

onUnmounted(() => {
  clearInterval(pourTimer)
  cancelAnimationFrame(animFrame)
  stopLobbyPolling()
})
</script>

<style scoped>
.pour-container {
  @apply relative w-full min-h-[480px] bg-[#0a0a14] overflow-hidden rounded-2xl;
}
.pour-canvas { @apply w-full block; touch-action: none; }
.round-info { @apply flex items-center justify-between px-4 py-1; }
.fill-hud { @apply text-center py-2; }
.overlay {
  @apply absolute inset-0 flex flex-col items-center justify-center z-20;
  background: rgba(10,10,20,0.92);
  backdrop-filter: blur(8px);
}
.start-btn {
  @apply px-8 py-3 bg-gradient-to-r from-amber-500 to-yellow-600 text-white font-bold rounded-xl;
  @apply hover:from-amber-400 hover:to-yellow-500 transition-all duration-200 active:scale-95;
}
.multiplayer-btn {
  @apply from-cyan-500 to-blue-600 hover:from-cyan-400 hover:to-blue-500;
}
.reward-box { @apply p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-xl text-center; }
.round-result {
  @apply absolute inset-0 flex flex-col items-center justify-center z-30 pointer-events-none;
  animation: resultPop 0.5s ease;
}
.result-perfect { color: #2ECC71; }
.result-good { color: #3498DB; }
.result-close { color: #F39C12; }
.result-miss { color: #95A5A6; }
.result-overflow { color: #E74C3C; }

.lobby-players { @apply flex flex-wrap justify-center gap-3 mt-2 mb-2; }
.lobby-player { @apply flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2; }
.lobby-avatar { @apply w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center text-white text-sm font-bold; }
.host-ring { @apply ring-2 ring-yellow-400; }

@keyframes resultPop {
  0% { transform: scale(0.5); opacity: 0; }
  50% { transform: scale(1.2); }
  100% { transform: scale(1); opacity: 1; }
}
.fade-enter-active { animation: resultPop 0.5s ease; }
.fade-leave-active { transition: opacity 0.3s; }
.fade-leave-to { opacity: 0; }
</style>
